import { t } from '../../../lib/i18n';
import { type AuctionScheduleItem } from '../types';
import { cn } from '../utils';
import { ListRow } from './list-row';

interface Props {
  auctions: AuctionScheduleItem[];
  loading?: boolean;
  className?: string;
}

/**
 * The closings board — the table the `Redesign-D-Admin` schedule screen is.
 *
 * Desktop is the artboard's six-column grid inside one bordered card that
 * scrolls horizontally below 860px of content width; mobile is the Mobile
 * artboard's stacked card list. One component, split at `lg`.
 */
export function AuctionScheduleListEditor({ auctions, loading, className }: Props) {
  if (loading) {
    return (
      <section
        className={cn(
          'overflow-hidden rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-6 lg:px-[18px]',
          className,
        )}
      >
        <p className="text-center text-[13px] text-cf-ink-subtle">
          {t('auctionSchedule.board.loading')}
        </p>
      </section>
    );
  }

  if (auctions.length === 0) {
    return (
      <section
        className={cn(
          'rounded-cf-lg border border-cf-line bg-cf-surface px-3.5 py-10 text-center lg:px-[18px]',
          className,
        )}
      >
        <h3 className="text-base font-semibold tracking-[-0.02em]">
          {t('auctionSchedule.list.empty')}
        </h3>
        <p className="mt-1.5 text-[13px] text-cf-ink-subtle">
          {t('auctionSchedule.list.emptyHint')}
        </p>
      </section>
    );
  }

  return (
    <section
      className={cn(
        'flex flex-col gap-2 lg:block lg:gap-0 lg:overflow-x-auto lg:rounded-cf-lg lg:border lg:border-cf-line lg:bg-cf-surface',
        className,
      )}
    >
      {/* Column heads belong to the desktop table only — the mobile artboard
          has cards, and a header over cards labels nothing. */}
      <div className="hidden lg:grid lg:min-w-[860px] lg:grid-cols-[120px_minmax(0,1fr)_100px_120px_130px_120px] lg:gap-4 lg:border-b lg:border-cf-line-soft lg:px-[18px] lg:py-2.5 lg:font-mono lg:text-[10px] lg:tracking-[0.12em] lg:text-cf-ink-subtle lg:uppercase">
        <span>{t('auctionSchedule.board.columns.date')}</span>
        <span>{t('auctionSchedule.board.columns.lot')}</span>
        <span>{t('auctionSchedule.board.columns.bid')}</span>
        <span>{t('auctionSchedule.board.columns.clientBids')}</span>
        <span>{t('auctionSchedule.board.columns.status')}</span>
        <span />
      </div>

      {auctions.map((auction) => (
        <ListRow key={auction._id} auction={auction} />
      ))}
    </section>
  );
}
